<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useAuthStore } from '@/stores/authStore.ts';
import validators from '@/utils/validators.ts';
import CardComponent from '@/components/ui/CardComponent.vue';

import type { ValidationRule } from '@/types/utils/validators.type.ts';
import AppLogoComponent from '@/components/ui/AppLogoComponent.vue';

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()

//--- Define page content ---//
const loading = ref<boolean>(false)

//--- Login form ---//
const formValid = ref<boolean>(false)
const showPassword = ref<boolean>(false)
const emailRules = ref<ValidationRule[]>([...validators.emailRules])
const passwordRules = ref<ValidationRule[]>([...validators.passwordRules])
const form = reactive({
  email: '',
  password: ''
})

const togglePasswordVisibility = (): void => {
  showPassword.value = !showPassword.value
}

async function submitLogin(): Promise<void> {
  loading.value = true

  const user = {
    email: form.email,
    password: form.password
  }

  try {
    await authStore.login(user);
    await router.push({
      path: '/login/otp',
      query: {
        email: form.email
      }
	  });
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <v-container fluid class="h-screen w-full d-flex justify-center align-center">
    <CardComponent>
      <template #title>
        <div class="d-flex align-center justify-center my-4">
          <AppLogoComponent />
        </div>
      </template>
      <template #item>
        <v-form v-model="formValid" @submit.prevent="submitLogin">
          <v-row class="py-1">
            <v-col cols="12">
              <v-text-field
                v-model="form.email"
                :label="t('common.inputs.email.label')"
                :placeholder="t('common.inputs.email.placeholder')"
                :rules="emailRules"
                autocomplete="email"
                hide-details="auto"
              />
            </v-col>
          </v-row>
          <v-row class="py-1">
            <v-col cols="12">
              <v-text-field
                v-model="form.password"
                :append-inner-icon="showPassword ? 'PhEyeSlash' : 'PhEye'"
                :label="t('common.inputs.password.label')"
                :placeholder="t('common.inputs.password.placeholder')"
                :rules="passwordRules"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="password"
                hide-details="auto"
                @click:append-inner="togglePasswordVisibility"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-row class="d-flex align-center justify-space-between">
                <v-col cols="12" md="6">
                  <RouterLink to="/forgot-password" class="text-decoration-none">
                    {{ t('views.login.buttons.forgotPassword') }}
                  </RouterLink>
                </v-col>
                <v-col cols="12" md="6">
                  <v-btn
                    :disabled="!formValid || loading"
                    :loading="loading"
                    block
                    type="submit"
                  >
                    {{ t('views.login.buttons.loginSubmit') }}
                    <template #append>
                      <v-icon icon="PhSignIn"/>
                    </template>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-form>
      </template>
    </CardComponent>
  </v-container>
</template>

<style scoped>
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
